import {
  type JWT,
  type SignInInput,
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  fetchAuthSession,
  fetchUserAttributes,
} from "aws-amplify/auth";
import { Amplify } from "aws-amplify";

import type { IAuthSignIn, ICheckToken } from "./authTypes";

type AuthFlowType = NonNullable<SignInInput["options"]>["authFlowType"];

export interface IIdToken {
  success: boolean;
  token?: JWT;
  message?: string;
}

let authFlowType: AuthFlowType | undefined;

export const configureCognito = (): void => {
  authFlowType = import.meta.env.VITE_COGNITO_AUTH_FLOW_TYPE as
    | AuthFlowType
    | undefined;

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: import.meta.env.VITE_COGNITO_USER_POOL,
        userPoolClientId: import.meta.env.VITE_COGNITO_APP_ID,
        ...(import.meta.env.VITE_COGNITO_ENDPOINT
          ? { userPoolEndpoint: import.meta.env.VITE_COGNITO_ENDPOINT }
          : {}),
      },
    },
  });
};

const errorMsg = (code?: string): string => {
  switch (code) {
    case "NotAuthorizedException": {
      return "The password you entered is incorrect. Please try again.";
    }
    case "UserNotFoundException": {
      return "The email you entered is not associated with an account. Please try again.";
    }
    default: {
      return "There was an error with the request.";
    }
  }
};

export const signIn = async (
  email: string,
  password: string
): Promise<IAuthSignIn> => {
  try {
    const { isSignedIn, nextStep } = await cognitoSignIn({
      username: email.trim().toLowerCase(),
      password,
      options: {
        authFlowType,
      },
    });

    if (nextStep.signInStep === "CONFIRM_SIGN_UP") {
      return {
        success: false,
        message: "The email is not verified.",
      };
    }

    if (!isSignedIn) {
      return {
        success: false,
        message: "There was an error with the request.",
      };
    }

    const attributes = await fetchUserAttributes();

    return {
      success: true,
      message: attributes,
    };
  } catch (error) {
    const code = error instanceof Error ? error.name : undefined;
    return {
      success: false,
      message: errorMsg(code),
    };
  }
};

export const signOut = async (): Promise<void> => {
  await cognitoSignOut();
};

export const getIdToken = async (): Promise<IIdToken> => {
  try {
    const { tokens } = await fetchAuthSession();
    return {
      success: true,
      token: tokens?.idToken,
    };
  } catch (error) {
    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : "Unable to fetch auth session.",
    };
  }
};

export const checkToken = async (): Promise<ICheckToken> => {
  try {
    const { tokens } = await fetchAuthSession();
    return {
      isValid: !!tokens?.idToken,
    };
  } catch (error) {
    return {
      isValid: false,
      message:
        error instanceof Error
          ? error.message
          : "Unable to fetch auth session.",
    };
  }
};

export const refreshSession = async (): Promise<IIdToken> => {
  try {
    const { tokens } = await fetchAuthSession({ forceRefresh: true });
    return {
      success: true,
      token: tokens?.idToken,
    };
  } catch (error) {
    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : "Unable to refresh auth session.",
    };
  }
};
